import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  Calendar,
  Users,
  FileText,
  MessageSquare,
  Plus,
  TrendingUp,
  AlertCircle,
  Wallet,
  AlertTriangle,
} from 'lucide-react';
import { useActiveClinic } from '@/lib/activeClinic';
import { useAppointmentsInRange } from '@/hooks/useAppointments';
import { usePatientsList } from '@/hooks/usePatients';
import { useInvoicesList } from '@/hooks/useInvoices';
import { formatBalance, useSmsClinic } from '@/hooks/useSms';
import { useClinicalAlerts } from '@/hooks/useClinicalAlerts';
import { useCurrentUserProfile } from '@/hooks/useCurrentUserProfile';

function startOfDayISO(date: Date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
}

function endOfDayISO(date: Date) {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d.toISOString();
}

function startOfWeekISO(date: Date) {
  const d = new Date(date);
  const day = d.getDay() || 7; // Mon=1..Sun=7
  if (day !== 1) d.setHours(-24 * (day - 1));
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
}

function endOfWeekISO(date: Date) {
  const d = new Date(date);
  const day = d.getDay() || 7;
  d.setHours(24 * (7 - day));
  d.setHours(23, 59, 59, 999);
  return d.toISOString();
}

function formatTime(iso: string) {
  return new Date(iso).toLocaleTimeString('da-DK', { hour: '2-digit', minute: '2-digit' });
}

function formatMoneyDkk(cents: number) {
  const v = (cents / 100).toLocaleString('da-DK', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${v} kr`;
}

export function Dashboard() {
  const { t } = useTranslation();
  const { activeClinicId } = useActiveClinic();
  const { data: profile } = useCurrentUserProfile();

  const today = useMemo(() => new Date(), []);

  // ---- Data ----
  const todayApts = useAppointmentsInRange(activeClinicId, {
    start: startOfDayISO(today),
    end: endOfDayISO(today),
  });

  const weekApts = useAppointmentsInRange(activeClinicId, {
    start: startOfWeekISO(today),
    end: endOfWeekISO(today),
  });

  const patientsQ = usePatientsList(activeClinicId, { status: 'active' });
  const invoicesQ = useInvoicesList(activeClinicId, { status: 'all' });
  const smsQ = useSmsClinic(activeClinicId);

  // ---- Derived ----
  const upcomingToday = (todayApts.data ?? []).filter(
    (a) => new Date(a.starts_at).getTime() >= today.getTime(),
  );
  const nextAppointment = upcomingToday[0] ?? null;

  const patientsCount = patientsQ.data?.length ?? 0;
  const weekAppointmentsCount = weekApts.data?.length ?? 0;

  const invoices = invoicesQ.data ?? [];
  const issuedInvoices = invoices.filter((i) => i.status === 'issued' || i.status === 'partial' || i.status === 'overdue');
  const overdueInvoices = invoices.filter((i) => i.status === 'overdue');
  const partialInvoices = invoices.filter((i) => i.status === 'partial');
  const outstandingTotal = issuedInvoices.reduce((sum, i) => sum + (i.total_cents - i.total_paid_cents), 0);
  const paidThisMonth = invoices
    .filter((i) => {
      if (i.status !== 'paid' || !i.paid_at) return false;
      const d = new Date(i.paid_at);
      return d.getMonth() === today.getMonth() && d.getFullYear() === today.getFullYear();
    })
    .reduce((sum, i) => sum + i.total_cents, 0);

  const clinicalAlertsQ = useClinicalAlerts(activeClinicId);
  const unackedAlerts = clinicalAlertsQ.data ?? [];

  const smsBalance = smsQ.data?.credits.balance ?? 0;
  const smsLowBalance =
    smsQ.data &&
    smsQ.data.credits.lowBalanceThreshold > 0 &&
    smsQ.data.credits.balance <= smsQ.data.credits.lowBalanceThreshold;

  const greeting = (() => {
    const h = today.getHours();
    if (h < 10) return t('dashboard.greeting_morning', 'Godmorgen');
    if (h < 18) return t('dashboard.greeting_afternoon', 'God eftermiddag');
    return t('dashboard.greeting_evening', 'God aften');
  })();

  const userName = profile?.name?.trim() ?? '';

  return (
    <div className="min-h-screen bg-page-light dark:bg-page-dark">
      <main className="py-8">
        <div className="mx-auto max-w-7xl px-6">
          {/* Header */}
          <div className="md:flex md:items-end md:justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                {greeting}
                {userName ? `, ${userName}` : ''}
              </h1>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {today.toLocaleDateString('da-DK', {
                  weekday: 'long',
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                })}
              </p>
            </div>
            <div className="mt-4 flex flex-wrap gap-2 md:mt-0">
              <Link
                to="/patients/new"
                className="inline-flex items-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-500"
              >
                <Plus className="mr-1 h-4 w-4" /> {t('dashboard.new_patient', 'Ny patient')}
              </Link>
              <Link
                to="/appointments"
                className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700"
              >
                <Plus className="mr-1 h-4 w-4" /> {t('dashboard.new_appointment', 'Ny aftale')}
              </Link>
              <Link
                to="/invoices/new"
                className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700"
              >
                <Plus className="mr-1 h-4 w-4" /> {t('dashboard.new_invoice', 'Ny faktura')}
              </Link>
            </div>
          </div>

          {/* KPI cards */}
          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <KpiCard
              icon={<Calendar className="h-5 w-5 text-indigo-500" />}
              label={t('dashboard.today_appointments', 'Aftaler i dag')}
              value={String(todayApts.data?.length ?? 0)}
              hint={
                todayApts.data && todayApts.data.length > 0
                  ? t('dashboard.next_at', 'Næste kl. {{time}}', {
                      time: nextAppointment ? formatTime(nextAppointment.starts_at) : '—',
                    })
                  : t('dashboard.no_appointments_today', 'Ingen aftaler i dag')
              }
              loading={todayApts.isLoading}
              to="/appointments"
            />
            <KpiCard
              icon={<Users className="h-5 w-5 text-emerald-500" />}
              label={t('dashboard.active_patients', 'Aktive patienter')}
              value={String(patientsCount)}
              hint={t('dashboard.this_clinic', 'På denne klinik')}
              loading={patientsQ.isLoading}
              to="/patients"
            />
            <KpiCard
              icon={<FileText className="h-5 w-5 text-amber-500" />}
              label={t('dashboard.outstanding_invoices', 'Udestående fakturaer')}
              value={String(issuedInvoices.length)}
              hint={
                overdueInvoices.length > 0 || partialInvoices.length > 0
                  ? t('dashboard.x_overdue_y_partial', '{{overdue}} forfaldne · {{partial}} delvise · {{total}}', {
                      overdue: overdueInvoices.length,
                      partial: partialInvoices.length,
                      total: formatMoneyDkk(outstandingTotal),
                    })
                  : formatMoneyDkk(outstandingTotal)
              }
              loading={invoicesQ.isLoading}
              accent={overdueInvoices.length > 0 ? 'warning' : undefined}
                to="/economy/invoices"
            />
            <KpiCard
              icon={<MessageSquare className="h-5 w-5 text-fuchsia-500" />}
              label={t('dashboard.sms_balance', 'SMS-saldo')}
              value={smsQ.data ? formatBalance(smsBalance) : '—'}
              hint={
                smsLowBalance
                  ? t('dashboard.low_balance', 'Lav saldo — fyld op')
                  : t('dashboard.sms_ready', 'Klar til afsending')
              }
              loading={smsQ.isLoading}
              accent={smsLowBalance ? 'warning' : undefined}
              to="/settings/sms"
            />
          </div>

          {/* Two-column section */}
          <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
            {/* Today's schedule */}
            <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-card-dark lg:col-span-2">
              <div className="flex items-center justify-between">
                <h2 className="text-base font-semibold text-gray-900 dark:text-white">
                  {t('dashboard.todays_schedule', 'Dagens program')}
                </h2>
                <Link
                  to="/appointments"
                  className="text-sm font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-400"
                >
                  {t('dashboard.view_calendar', 'Vis kalender →')}
                </Link>
              </div>

              {todayApts.isLoading ? (
                <p className="mt-6 text-sm text-gray-500">…</p>
              ) : (todayApts.data ?? []).length === 0 ? (
                <div className="mt-8 text-center text-sm text-gray-500 dark:text-gray-400">
                  <Calendar className="mx-auto h-10 w-10 text-gray-300" />
                  <p className="mt-2">
                    {t('dashboard.no_appointments_today', 'Ingen aftaler i dag')}
                  </p>
                  <Link
                    to="/appointments"
                    className="mt-3 inline-flex items-center text-sm font-medium text-indigo-600 hover:text-indigo-500"
                  >
                    <Plus className="mr-1 h-4 w-4" /> {t('dashboard.new_appointment', 'Ny aftale')}
                  </Link>
                </div>
              ) : (
                <ul className="mt-4 divide-y divide-gray-100 dark:divide-gray-700">
                  {(todayApts.data ?? []).map((a) => {
                    const end = new Date(a.ends_at);
                    const isPast = end.getTime() < Date.now();
                    return (
                      <li
                        key={a.id}
                        className={`flex items-center gap-3 py-3 ${isPast ? 'opacity-60' : ''}`}
                      >
                        <div
                          className="h-9 w-1 rounded-full"
                          style={{ backgroundColor: a.service_color ?? '#6366f1' }}
                        />
                        <div className="w-20 shrink-0 font-mono text-sm text-gray-700 dark:text-gray-200">
                          {formatTime(a.starts_at)}
                          <span className="mx-1 text-gray-300">–</span>
                          {formatTime(a.ends_at)}
                        </div>
                        <div className="min-w-0 flex-1">
                          <Link
                            to={`/patients/${a.patient_id}`}
                            className="block truncate text-sm font-medium text-gray-900 hover:text-indigo-600 dark:text-white dark:hover:text-indigo-400"
                          >
                            {a.patient_name}
                          </Link>
                          <p className="truncate text-xs text-gray-500 dark:text-gray-400">
                            {a.service_name ?? t('dashboard.no_service', 'Ingen service')}
                            {a.clinician_name ? ` · ${a.clinician_name}` : ''}
                          </p>
                        </div>
                        <span
                          className={`inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                            a.status === 'completed'
                              ? 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                              : a.status === 'cancelled'
                                ? 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                                : 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                          }`}
                        >
                          {t(`dashboard.appt_status_${a.status}`, a.status)}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>

            {/* Right column: revenue + alerts */}
            <section className="space-y-6">
              <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-card-dark">
                <div className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-emerald-500" />
                  <h2 className="text-base font-semibold text-gray-900 dark:text-white">
                    {t('dashboard.month_summary', 'Denne måned')}
                  </h2>
                </div>
                <dl className="mt-3 space-y-3">
                  <div>
                    <dt className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      {t('dashboard.paid', 'Betalt')}
                    </dt>
                    <dd className="text-2xl font-semibold text-gray-900 dark:text-white">
                      {formatMoneyDkk(paidThisMonth)}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      {t('dashboard.appointments_this_week', 'Aftaler denne uge')}
                    </dt>
                    <dd className="text-2xl font-semibold text-gray-900 dark:text-white">
                      {weekAppointmentsCount}
                    </dd>
                  </div>
                </dl>
              </div>

              {(overdueInvoices.length > 0 || partialInvoices.length > 0 || smsLowBalance) && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-5 shadow-sm dark:border-amber-800 dark:bg-amber-900/20">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                    <h2 className="text-base font-semibold text-amber-900 dark:text-amber-200">
                      {t('dashboard.alerts', 'Påmindelser')}
                    </h2>
                  </div>
                  <ul className="mt-3 space-y-2 text-sm text-amber-900 dark:text-amber-200">
                    {overdueInvoices.length > 0 && (
                      <li className="flex items-start gap-2">
                        <FileText className="mt-0.5 h-4 w-4 shrink-0" />
                        <Link to="/economy/invoices" className="hover:underline">
                          {t(
                            'dashboard.x_overdue_invoices',
                            '{{n}} forfaldne fakturaer venter på betaling',
                            {
                              n: overdueInvoices.length,
                            },
                          )}
                        </Link>
                      </li>
                    )}
                    {partialInvoices.length > 0 && (
                      <li className="flex items-start gap-2">
                        <FileText className="mt-0.5 h-4 w-4 shrink-0" />
                        <Link to="/economy/invoices" className="hover:underline">
                          {t(
                            'dashboard.x_partial_invoices',
                            '{{n}} fakturaer med delvis betaling',
                            {
                              n: partialInvoices.length,
                            },
                          )}
                        </Link>
                      </li>
                    )}
                    {smsLowBalance && (
                      <li className="flex items-start gap-2">
                        <Wallet className="mt-0.5 h-4 w-4 shrink-0" />
                        <Link to="/settings/sms" className="hover:underline">
                          {t('dashboard.sms_low', 'SMS-saldo er lav — overvej at fylde op')}
                        </Link>
                      </li>
                    )}
                  </ul>
                </div>
              )}

              {/* Clinical Alert Center */}
              {unackedAlerts.length > 0 && (
                <div className="rounded-xl border border-red-200 bg-red-50 p-5 shadow-sm dark:border-red-800 dark:bg-red-900/20">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400" />
                      <h2 className="text-base font-semibold text-red-900 dark:text-red-200">
                        {t('dashboard.clinical_alerts', 'Kliniske advarsler')}
                      </h2>
                    </div>
                    <span className="inline-flex items-center rounded-full bg-red-600 px-2 py-0.5 text-xs font-bold text-white">
                      {unackedAlerts.length}
                    </span>
                  </div>
                  <ul className="mt-3 space-y-2">
                    {unackedAlerts.slice(0, 5).map((alert) => (
                      <li key={alert.id} className="flex items-start gap-2 text-sm text-red-900 dark:text-red-200">
                        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                        <Link
                          to={`/patients/${alert.patient_id}`}
                          className="hover:underline"
                        >
                          {alert.alert_data?.template_name ?? t('dashboard.clinical_alert_unknown', 'Ukendt skema')}
                          {' — '}
                          {t('dashboard.clinical_alert_score', 'score {{score}}', {
                            score: alert.alert_data?.total_score ?? '?',
                          })}
                        </Link>
                      </li>
                    ))}
                    {unackedAlerts.length > 5 && (
                      <li className="text-xs text-red-700 dark:text-red-300">
                        {t('dashboard.clinical_alerts_more', '+ {{n}} flere', {
                          n: unackedAlerts.length - 5,
                        })}
                      </li>
                    )}
                  </ul>
                </div>
              )}
            </section>
          </div>
        </div>
      </main>
    </div>
  );
}

function KpiCard({
  icon,
  label,
  value,
  hint,
  loading,
  accent,
  to,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  hint?: string;
  loading?: boolean;
  accent?: 'warning';
  to?: string;
}) {
  const body = (
    <div
      className={`rounded-xl border bg-white p-5 shadow-sm transition-shadow dark:bg-card-dark ${
        accent === 'warning'
          ? 'border-amber-200 dark:border-amber-800'
          : 'border-gray-200 dark:border-gray-700'
      } ${to ? 'cursor-pointer hover:shadow-md' : ''}`}
    >
      <div className="flex items-center gap-2">
        {icon}
        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</h3>
      </div>
      <p className="mt-2 text-2xl font-semibold text-gray-900 dark:text-white">
        {loading ? '…' : value}
      </p>
      {hint && (
        <p
          className={`mt-1 text-xs ${
            accent === 'warning'
              ? 'text-amber-700 dark:text-amber-300'
              : 'text-gray-500 dark:text-gray-400'
          }`}
        >
          {hint}
        </p>
      )}
    </div>
  );

  return to ? <Link to={to}>{body}</Link> : body;
}
